import { type CSSProperties, css, cx } from '@linaria/core'
import type { FC, HTMLProps } from 'react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { IconButton } from '#components/IconButton'
import CollapseBackLeftIcon from '#icons/collapse-back-left-48.svg'
import CollapseBackRightIcon from '#icons/collapse-back-right-48.svg'
import CollapseLeftIcon from '#icons/collapse-left-48.svg'
import CollapseRightIcon from '#icons/collapse-right-48.svg'
import type { SidepanelsBehavior } from '#store/componentConfiguration'
import { type Collapse, setPanelCollapse } from '#store/layoutConfiguration'
import { useSharedAppDispatch, useSharedAppSelector } from '#store/sharedBaseStore'
import { style } from '#styles'
import { useWindowSize } from '#utils/useWindowResize'

const foldoutClass = css`
  @media print {
    display: none;
  }
  --target-width: 314px;

  position: relative;

  min-width: 60px;
  height: 100%;
  z-index: 1;
  isolation: isolate;
  
  // controlling the placement and sized-ness of the aside
  width: 60px;
  &._spaced {
    width: var(--target-width);
  }
  
  &._spaced,
  &:hover {
    > ._fly-over {
      width: var(--target-width);
    }
  }

  > ._fly-over {
    position: absolute;
    display: flex;
    flex-direction: column;
    height: 100%;
    width: 60px;
    // important to keep the possibly visible elements
    // from overlapping into other content next to the sidebar
    overflow: hidden;

    background-color: color-mix(in srgb, ${style.themeColors.backgroundDefault} 60%, transparent);
    backdrop-filter: blur(5px);
    transition: width 0.25s ease;

    > ._fly-over-content {
      position: absolute;
      height: 100%;
      width: 100%;
    }

    > ._view {
      display: flex;
      flex-direction: column;
      flex: 1;
      overflow-y: scroll;
      overflow-x: hidden;

      width: var(--target-width);
      will-change: opacity;
      transition: opacity 0.15s ease;

      &._collapsed {
        opacity: 0;
      }

      &._expanded {
        opacity: 1;
      }

      & + :last-child {
        padding-bottom: 20px;
      }
      
      &.no-scroll {
        overflow: hidden;
      }
    }
    

    > ._collapsing {
      width: 100%;
      height: 23px;
      margin: 0px 0 16px 0;
      align-self: flex-start;
      transition: width 0.25s ease;
      display: flex;
      flex-direction: row;
    }
  }

  > ._icon-holder {
    position: absolute;
    bottom: 20px;
  }

  &[data-side='left'] {
    > ._fly-over {
      border-right: 2px solid ${style.themeColors.lineDefault};
      left: 0;

      > ._collapsing {
        justify-content: flex-start;
      }
    }

    > ._icon-holder {
      left: 20px;
    }
  }

  &[data-side='right'] {
    > ._fly-over {
      border-left: 2px solid ${style.themeColors.lineDefault};
      right: 0;

      > ._collapsing {
        justify-content: flex-end;
      }
    }

    > ._icon-holder {
      right: 20px;
    }
  }
`

const iconSet = {
  left: [CollapseLeftIcon, CollapseBackLeftIcon],
  right: [CollapseRightIcon, CollapseBackRightIcon],
} as const

export interface AsideComponentProps extends HTMLProps<HTMLElement> {
  side: 'left' | 'right'
  /**
   * The view class gets sized by the component.
   * It gets the classes `_collapsed` or `_expanded` attached, resulting in a
   * class construct of `_view _expanded yourclassname` or `_view _collapsed yourclassname`
   *
   * @example
   * // Allowing view controls for specific styling when the component is expanded or collapsed
   * const myClass = css`
   *    &._collapsed {
   *      transform: translateX(314px);
   *    }
   * `
   * <AsideComponent viewClassName={myClass} />
   */
  viewClassName?: string
  width?: number
}

const useSidepanel = (
  screenSize: ReturnType<typeof useWindowSize>,
  forceMode: SidepanelsBehavior,
  collapse: Collapse | undefined,
  isMouseEntered: boolean,
  side: 'left' | 'right',
) => {
  return useMemo(() => {
    // if the collapse mode is pushed out anyway, it stays like that. Otherwise the mouse decides if it should be shown.
    const isCollapsed = collapse === 'visible' ? ('visible' as const) : isMouseEntered ? ('visible' as const) : ('collapsed' as const)
    // select the icon based on side and the state of the panel, only relevant on foldouts
    const [collapseIcon, collapseBackIcon] = iconSet[side]
    const icon = isCollapsed === 'visible' ? collapseBackIcon : collapseIcon

    let mode: 'foldout' | 'static' | 'popover'
    switch (forceMode) {
      case 'force-foldout':
        mode = 'foldout'
        break
      case 'force-popover':
        mode = 'popover'
        break
      case 'disable':
        mode = 'static'
        break
      case 'switching':
        mode = screenSize.size === 's' ? 'popover' : 'foldout'
    }

    switch (mode) {
      case 'foldout': {
        const isCollapsed = !isMouseEntered && collapse === 'collapsed'
        return {
          behavior: 'foldout',
          isCollapsed,
          reserveAsideSpace: collapse === 'visible',
          icon,
        }
      }
      case 'static': {
        return {
          behavior: 'static',
          isCollapsed: false,
          reserveAsideSpace: true,
          icon: undefined,
        }
      }
      case 'popover': {
        return {
          behavior: 'popover',
          isCollapsed: !isMouseEntered,
          reserveAsideSpace: false,
          icon: undefined,
        }
      }
    }
  }, [screenSize, forceMode, collapse, isMouseEntered, side])
}

export const AsideComponent: FC<AsideComponentProps> = ({
  children,
  className,
  viewClassName,
  side,
  width = 314,
  onMouseEnter,
  onMouseMove,
  onMouseLeave,
  onTouchStart,
  onTouchMove,
  onTouchEnd,
  ...props
}) => {
  const dispatch = useSharedAppDispatch()
  const { t } = useTranslation('shared')

  const [mouseEntered, setMouseEntered] = useState(false)
  const mouseEnter = () => {
    setMouseEntered(true)
  }
  const mouseExit = () => {
    setMouseEntered(false)
  }

  const configuredForceMode = useSharedAppSelector((x) => x.componentConfiguration.sidepanelBehavior)
  const forceMode = configuredForceMode ?? 'switching'
  const screenSize = useWindowSize()
  const collapsed = useSharedAppSelector((x) => x.layoutConfiguration.panels[side])

  const { isCollapsed, icon, reserveAsideSpace } = useSidepanel(screenSize, forceMode, collapsed, mouseEntered, side)

  return (
    <aside
      className={cx(foldoutClass, reserveAsideSpace ? '_spaced' : '_trimmed', className)}
      style={{ '--target-width': `${width}px` } as CSSProperties}
      data-side={side}
      onMouseEnter={(evt) => {
        mouseEnter()
        onMouseEnter?.(evt)
      }}
      onMouseMove={(evt) => {
        mouseEnter()
        onMouseEnter?.(evt)
      }}
      onMouseLeave={(evt) => {
        mouseExit()
        onMouseLeave?.(evt)
      }}
      onTouchStart={(evt) => {
        mouseEnter()
        onTouchStart?.(evt)
      }}
      onTouchMove={(evt) => {
        mouseEnter()
        onTouchMove?.(evt)
      }}
      onTouchEnd={(evt) => {
        mouseExit()
        onTouchEnd?.(evt)
      }}
      {...props}
    >
      <div className="_fly-over">
        <div className={cx('_view', isCollapsed ? '_collapsed' : '_expanded', viewClassName)}>{children}</div>
        {icon && <div className="_collapsing" />}
      </div>
      {icon && (
        <div className="_icon-holder">
          <IconButton
            iconSize="s"
            className="_icon"
            title={isCollapsed ? t('common.button.expand', 'Expand') : t('common.button.collapse', 'Collapse')}
            iconUrl={icon}
            onClick={() => {
              dispatch(
                setPanelCollapse({
                  panel: side,
                  collapse: collapsed === 'collapsed' ? 'visible' : 'collapsed',
                }),
              )
              window.focus()
            }}
          />
        </div>
      )}
    </aside>
  )
}
